import { format, parseISO } from "date-fns";
import pluralize from "pluralize";
import React, { useCallback, useState } from "react";
import { Button, Icon } from "../../../components";
import { BookingType, PricingType } from "../../../data";
import { useI18n } from "../../../lib/i18n/i18n_provider";
import { Spacer } from "../../spacer";
import { differenceInMinutes, parseTime, TimeInterval } from "../../time_utils";
import { FAKE_MEMBERSIP_MEETING_ROOM_DETAILS } from "../fake_data";
import { usePortalHistory } from "../hooks/use_portal_history";
import { InviteAttendeesDialog } from "./invite_attendees_dialog";
import { PortalModal } from "./portal_modal";
import { PortalBookingStep1 } from "./portal_booking_step_1";
import { PortalBookingStep2 } from "./portal_booking_step_2";
import { PortalBookingStep3 } from "./portal_booking_step_3";
import { PortalButton } from "./portal_button";
import { PortalImageCard } from "./portal_image_card";
import styles from "./portal_meeting_room_list.module.css";
import { useDateTimePickerHelper } from "../../order_params";
import { useGetBetterHourRatesQuery } from "../../../offering_v2/hooks/use_better_hour_rates_query";
import {
  getViewRange,
  useBestDayRatesQuery,
} from "../../../offering_v2/hooks/use_best_day_rates_query";
import { toBestDateRates } from "../../../offering_v2/utils/to_best_date_rates";
import { useSpaceQuery } from "../../api";
import { formatCurrency } from "../../../lib/currency/currency";
import { getSystemLocale } from "../../../lib/locale/locale";
import { toDay } from "../../day_utils";
import { TimeRangePicker } from "../../../offering_v2/components/date-time-picker/checkout_datetime_picker";
import {
  DateRangePicker,
  PickingRange,
} from "../../../offering_v2/components/date_range_picker/date_range_picker";

interface PortalMeetingRoom {
  id: string;
  imgUrl: string;
  name: string;
  location: string;
  capacity: number;
  offeringSlug: string;
  spaceId: string;
}

interface PortalMeetingRoomListProps {
  rooms: PortalMeetingRoom[];
  partnerId: string;
}

export function PortalMeetingRoomList({
  rooms,
  partnerId,
}: PortalMeetingRoomListProps) {
  const { toOfferingDetailsPage } = usePortalHistory();

  const [openBookingDialog, setOpenBookingDialog] = useState(false);

  return (
    <div>
      <div className={styles.list}>
        {rooms.map((r) => (
          <PortalImageCard
            key={r.id}
            imgAlt={r.name}
            imgHeight={209}
            imgUrl={r.imgUrl}
            details={
              <div>
                <div className={styles.name}>{r.name}</div>
                <div>{r.location}</div>
                <Spacer size={32} />
                <div>
                  <Icon name="people" /> Up to {r.capacity}{" "}
                  {pluralize("person", r.capacity)}
                </div>
                <Spacer size={32} />
                <PortalButton onClick={() => setOpenBookingDialog(true)}>
                  Book here
                </PortalButton>
                <Spacer size={16} />
                <div className={styles.viewRoomBtnWrapper}>
                  <PortalButton
                    appearance="text"
                    onClick={() =>
                      toOfferingDetailsPage(r.offeringSlug, r.spaceId)
                    }
                  >{`View ${r.name} details`}</PortalButton>
                </div>
              </div>
            }
          />
        ))}
      </div>

      {openBookingDialog && (
        <PortalMeetingRoomBookingDialog
          onHide={() => setOpenBookingDialog(false)}
          partnerId={partnerId}
        />
      )}
    </div>
  );
}

interface PortalMeetingRoomBookingDialogProps {
  onHide: () => void;
  partnerId: string;
}

function PortalMeetingRoomBookingDialog({
  onHide,
  partnerId,
}: PortalMeetingRoomBookingDialogProps) {
  const { fbt } = useI18n();

  const [currentStep, setCurrentStep] = useState(0);
  const [date, setDate] = useState<Date>();
  const [startTime, setStartTime] = useState<string>();
  const [endTime, setEndTime] = useState<string>();

  const [openInviteDialog, setOpenInviteDialog] = useState(false);

  const [viewingRange, setViewingRange] = useState<{
    startDate: Date;
    endDate: Date;
  }>();

  const { data: space } = useSpaceQuery(
    FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.spaceId
  );
  const spacePrice = space?.pricings.find(
    (p) => p.type === PricingType.Hourly
  )?.price;
  const formatedBasePrice =
    spacePrice && space?.currencyCode
      ? formatCurrency(spacePrice, space.currencyCode, getSystemLocale())
      : "";
  const priceRanges = space?.priceRanges.hourly;
  const hasPriceRange = priceRanges?.highestPrice !== priceRanges?.lowestPrice;
  const formatedPriceRanges =
    priceRanges && hasPriceRange
      ? `${formatCurrency(
          priceRanges.lowestPrice,
          space?.currencyCode || null,
          getSystemLocale()
        )}-${Math.round(priceRanges.highestPrice)}`
      : undefined;

  const { handleNextVisibleDates, timeslots, availableDays, handleSelectDate } =
    useDateTimePickerHelper(FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.spaceId, {
      dateRange: {
        startDate: date ? toDay(date) : undefined,
        endDate: date ? toDay(date) : undefined,
      },
    });

  const { data: bestDayRates } = useBestDayRatesQuery(
    getViewRange(
      viewingRange,
      date && {
        startDate: toDay(date),
        endDate: toDay(date),
      }
    ),
    BookingType.Hourly,
    FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.spaceId
  );

  const betterHourRates = useGetBetterHourRatesQuery(
    timeslots,
    date
      ? bestDayRates.options[date.toLocaleDateString()] || spacePrice
      : undefined,
    { date: date && toDay(date), startTime, endTime },
    FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.spaceId,
    space?.currencyCode
  );

  let formatedBookingDate = date && startTime && endTime && (
    <div>
      {format(date, "MMMM dd, yyyy")}
      <br />
      {fbt.formatTime(parseTime(startTime))} -{" "}
      {fbt.formatTime(parseTime(endTime))}
    </div>
  );
  let numberOfSelectedHours =
    startTime && endTime ? differenceInMinutes(endTime, startTime) / 60 : 0;

  const doExceedHours =
    FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.usedCount + numberOfSelectedHours >
    FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.totalCount;
  const noRemainingHours =
    FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.usedCount >=
    FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.totalCount;
  const showDynamicPricing = doExceedHours || noRemainingHours;

  const handleClickInviteAttendees = () => {
    onHide();
    setOpenInviteDialog(true);
  };

  const handleChangeTime = ({ start, end }: Partial<TimeInterval>) => {
    setStartTime(start);
    setEndTime(end);
  };

  const handleViewDates = useCallback(
    (startDate: Date, endDate: Date) => {
      setViewingRange({ startDate, endDate });
      handleNextVisibleDates(startDate, endDate);
    },
    [handleNextVisibleDates]
  );

  const handleChange = (value: { startDate: Date }) => {
    setDate(value.startDate);
    handleSelectDate(value.startDate);
  };

  const steps = [
    {
      title: "Select a Date and Time",
      content: (
        <PortalBookingStep1
          title={FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.name}
          description={FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.description}
          warningMessage="You will now be charged for each hour that exceeds your remaining membership hours."
          usedCount={FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.usedCount}
          totalCount={FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.totalCount}
          selectedCount={numberOfSelectedHours}
          unitPrice={hasPriceRange ? formatedPriceRanges : formatedBasePrice}
          spaceType="meeting_room"
          rightContent={
            <div>
              <DateRangePicker
                pickingRange={PickingRange.OneDate}
                value={{ startDate: date, endDate: date }}
                availableDates={availableDays?.map((d) => parseISO(d.date))}
                dynamicPricings={toBestDateRates(
                  bestDayRates.options,
                  betterHourRates.dynamicPrice,
                  space?.currencyCode
                )}
                onChange={handleChange}
                onViewDates={handleViewDates}
              />
              <div>{betterHourRates.recommendation}</div>
              <div className="time-range-picker d-flex m-3">
                <TimeRangePicker
                  value={{ start: startTime, end: endTime }}
                  onChangeTime={handleChangeTime}
                  timeslots={
                    showDynamicPricing ? betterHourRates.timeSlots : timeslots
                  }
                />
              </div>
            </div>
          }
        />
      ),
      footer: (
        <Button
          title="Continue"
          onPress={() => setCurrentStep(1)}
          disabled={!(date && startTime && endTime)}
        />
      ),
    },
    {
      title: "Confirm Booking",
      content: (
        <PortalBookingStep2
          spaceName={FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.name}
          capacity={FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.capacity}
          imgUrl={FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.imageUrl}
          location={FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.location}
          usedCount={
            FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.usedCount +
            numberOfSelectedHours
          }
          remainingCount={
            FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.totalCount -
            (FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.usedCount +
              numberOfSelectedHours)
          }
          bookingDate={formatedBookingDate}
          bookingType={BookingType.Hourly}
          partnerId={partnerId}
        />
      ),
      footer: (
        <Button title="Confirm booking" onPress={() => setCurrentStep(2)} />
      ),
    },
    {
      title: "Booking Confirmation",
      content: (
        <PortalBookingStep3
          spaceName={FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.name}
          capacity={FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.capacity}
          imgUrl={FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.imageUrl}
          location={FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.location}
          usedCount={
            FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.usedCount +
            numberOfSelectedHours
          }
          remainingCount={
            FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.totalCount -
            (FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.usedCount +
              numberOfSelectedHours)
          }
          bookingDate={formatedBookingDate}
          bookingEmail={FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.bookingEmail}
          bookingType={BookingType.Hourly}
        />
      ),
      footer: (
        <Button title="Invite attendees" onPress={handleClickInviteAttendees} />
      ),
    },
  ];

  return (
    <div>
      <PortalModal
        show={true}
        onHide={onHide}
        title={steps[currentStep].title}
        body={steps[currentStep].content}
        footer={steps[currentStep].footer}
        onClickBackButton={
          currentStep > 0 ? () => setCurrentStep((v) => v - 1) : undefined
        }
      />
      <InviteAttendeesDialog
        show={openInviteDialog}
        onHide={() => setOpenInviteDialog(false)}
        numberOfAttendees={FAKE_MEMBERSIP_MEETING_ROOM_DETAILS.capacity}
        invitedAttendees={[]}
      />
    </div>
  );
}
